import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { SpinnerProps } from '@magicschool/spellbook/components/Spinner';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import * as AvatarPrimitive from 'radix-ui/avatar';
import { useEffect } from 'react';

export const AvatarImageSlot = Slot<React.ComponentProps<typeof AvatarPrimitive.Image> & { isLoading?: boolean; label?: string }>({
  displayName: 'Avatar.Image',
  preventOverride: true,
  isRenderedByDefault: true,
  renderDefault: ({ className, isLoading, label, src, children, alt, 'aria-label': ariaLabel, ...props }) => {
    if (isLoading || !src) {
      return null;
    }

    // Determine alt and aria-label based on what's provided:
    // - If both alt and label: alt → alt, label → aria-label
    // - If only one: it goes to alt only
    // - If aria-label and label: aria-label → aria-label, label → alt
    // - If all 3: alt → alt, aria-label → aria-label
    const altValue = alt ?? label ?? '';
    const ariaLabelValue = ariaLabel ?? (alt && label ? label : undefined);
    const shouldHide = !alt && !label && !ariaLabel;

    return (
      <AvatarPrimitive.Image
        data-spellbook-name="avatar-image"
        className={className}
        src={src}
        alt={altValue}
        aria-label={ariaLabelValue}
        aria-hidden={shouldHide ? true : undefined}
        {...props}
      />
    );
  },
});

export const AvatarSpinnerSlot = Slot<SpinnerProps>({
  displayName: 'Avatar.Spinner',
  isRenderedByDefault: true,
  renderDefault: (props) => <Spinner data-spellbook-name="avatar-spinner" {...props} />,
});

export const AvatarIconSlot = Slot<IconProps & { label?: string; alt?: string }>({
  displayName: 'Avatar.Icon',
  isRenderedByDefault: true,
  renderDefault: ({ name = 'user', size, className, color = 'inherit', label, alt, 'aria-label': ariaLabel, ...props }) => {
    useEffect(() => {
      if (alt) {
        console.warn(
          "[Spellbook][Avatar.Icon]: The 'alt' prop is not standard for SVG icons. Please use 'aria-label' or 'label' props for accessibility instead.",
        );
      }
    }, [alt]);

    const ariaLabelValue = ariaLabel ?? label ?? alt ?? undefined;
    const shouldHide = !ariaLabel && !label && !alt;

    return (
      <Icon
        data-spellbook-name="avatar-icon"
        name={name}
        size={size}
        className={className}
        color={color}
        {...props}
        aria-label={ariaLabelValue}
        aria-hidden={shouldHide ? true : undefined}
        role={ariaLabelValue ? 'img' : 'presentation'}
      />
    );
  },
});
